import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { RouterOutputs } from '../../lib/trpc';
import { Panel } from '../common/Panel';
import {
  axisProps,
  CHART_MARGIN,
  formatCompactKr,
  formatWholeKr,
  gridProps,
  tooltipContentStyle,
  tooltipItemStyle,
  tooltipLabelStyle,
  useIsSmUp,
} from './chart';

type Forecast = RouterOutputs['insights']['monthForecast'];

/** Cumulative spending per day of the period, this period against the previous one. */
export function PaceChartCard({ forecast }: { forecast: Forecast }) {
  const isSmUp = useIsSmUp();
  const { cumulative } = forecast;
  const currentLabel = cumulative.currentLabel || 'Denna period';
  const previousLabel = cumulative.previousLabel || 'Förra perioden';

  const data =
    cumulative.previous.length > 0
      ? Array.from(
          { length: Math.max(cumulative.current.length, cumulative.previous.length) },
          (_, i) => ({
            day: i + 1,
            current: cumulative.current[i] ?? null,
            previous: cumulative.previous[i] ?? null,
          }),
        )
      : [];

  return (
    <Panel title="Utgiftstakt" description="Ackumulerat per dag, mot förra perioden">
      {data.length > 0 ? (
        <>
          <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <LegendItem label={currentLabel} color="var(--chart-1)" />
            <LegendItem label={previousLabel} color="var(--muted-foreground)" dashed />
          </div>
          <ResponsiveContainer width="100%" height={isSmUp ? 240 : 200}>
            <LineChart data={data} margin={CHART_MARGIN}>
              <CartesianGrid {...gridProps} vertical={false} />
              <XAxis
                {...axisProps}
                dataKey="day"
                interval="preserveStartEnd"
                minTickGap={isSmUp ? 16 : 8}
              />
              <YAxis {...axisProps} width={isSmUp ? 48 : 36} tickFormatter={formatCompactKr} />
              <Tooltip
                formatter={(value, name) => [formatWholeKr(value), name]}
                labelFormatter={(label) => `Dag ${label}`}
                contentStyle={tooltipContentStyle}
                itemStyle={tooltipItemStyle}
                labelStyle={tooltipLabelStyle}
                cursor={{ stroke: 'var(--border)' }}
              />
              <Line
                type="monotone"
                name={previousLabel}
                dataKey="previous"
                stroke="var(--muted-foreground)"
                strokeWidth={1.5}
                strokeDasharray="4 4"
                dot={false}
                activeDot={{ r: 3 }}
              />
              <Line
                type="monotone"
                name={currentLabel}
                dataKey="current"
                stroke="var(--chart-1)"
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 4 }}
                connectNulls={false}
              />
            </LineChart>
          </ResponsiveContainer>
          {forecast.period.isCurrent && (
            <p className="mt-2 text-xs text-muted-foreground">Den heldragna linjen slutar idag.</p>
          )}
        </>
      ) : (
        <p className="py-8 text-center text-sm text-muted-foreground">
          Det finns ingen tidigare period att jämföra med än.
        </p>
      )}
    </Panel>
  );
}

function LegendItem({ label, color, dashed }: { label: string; color: string; dashed?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        aria-hidden
        className="inline-block w-4 border-t-2"
        style={{ borderColor: color, borderStyle: dashed ? 'dashed' : 'solid' }}
      />
      <span className="capitalize">{label}</span>
    </span>
  );
}
